import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Modal,
  TextField,
} from '@material-ui/core';
import type { LaunchPadData, PriceRuleData } from '@mogul/api-client';
import { checkmark } from '@mogul/shared';
import type { BytesLike } from 'ethers';
import { ethers } from 'ethers';
import { useEffect, useState } from 'react';

import { ReactComponent as XIcon } from '../../../common/svg/general/icon-x.svg';
import { useWeb3State } from '../../../hooks';
import {
  useERC1155MintableLaunchpadContract,
  useLaunchpadContract,
} from '../../../hooks/launchpad-contract.hooks';
import { useStarsContract } from '../../../hooks/stars-contract.hooks';
import styles from './MintingModal.module.css';

enum messageColors {
  failure = 'red',
  success = '#fbd357',
}

enum buttonPurposes {
  mint = 'MINT',
  approveStars = 'APPROVE MOGUL',
}

export interface Props {
  contractAddress: string | undefined;
  launchPadData: LaunchPadData | undefined;
  amount: string;
  maxAmount: number;
  chainId: string;
  prices: number[] | undefined;
  currency: string[] | undefined;
  isPreSale: boolean;
  proof: BytesLike[];
  hideModal: () => void;
}

interface MintingModalProps {
  hideModal: () => void;
}

const MintingModal: React.FC<Props> = props => {
  const [amount, setAmount] = useState('0');
  const [loading, setLoading] = useState(false);
  const [mintingMessage, setMintingMessage] = useState('');
  const [messageColor, setMessageColor] = useState(messageColors.success);
  const [buttonPurpose, setButtonPurpose] = useState(buttonPurposes.mint);
  const [mintHash, setMintHash] = useState('');
  const [currencyOption, setCurrencyOption] = useState<string>('MOGUL');
  const [tierSelection, setTierSelection] = useState<number>(0);
  // The tokenId that we are going to start our mint with
  const [targetTokenId, setTargetTokenId] = useState<number>(0);
  const [cantMint, setCantMint] = useState<boolean>(false);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  // This locks minting completely on Modal load
  const [mintLock, setMintLock] = useState<boolean>(false);
  // Holds tier information
  const [tiers, setTiers] = useState<PriceRuleData[]>([]);

  // Array containing supply for every tokenId that has been minted
  const [_, setTotalSupplies] = useState<number[]>();

  const { walletDetails } = useWeb3State();

  const { mint, mintPreSale, contractInitialized } = useLaunchpadContract();
  const { launchPadData } = props;

  const {
    mintLaunchpadTokens,
    ERC1155ContractInitialized,
    getTotalSupplies,
    getUserBalances,
    getTokenIdSupply,
  } = useERC1155MintableLaunchpadContract({
    launchPadContractAddress: launchPadData?.collection?.contractAddress ?? '',
  });

  const { approve, allowance, initialized, starsAddress } = useStarsContract();

  const loadTierData = () => {
    if (launchPadData?.collection !== null && launchPadData !== undefined) {
      const { maxSupply, mintCurrency, mintPrice, pricingRules, saleStrategy } =
        launchPadData.collection;

      const tiersMapping: { [key: number]: PriceRuleData } = {};
      const tiersMapped: number[] = [];

      pricingRules?.forEach(tier => {
        tiersMapping[tier.from] = tier;
        tiersMapped.push(tier.from);
      });

      if (saleStrategy === 'Tiered') {
        const rules: PriceRuleData[] = [];

        for (let x = 0; x < parseInt(`${maxSupply ?? 0}`, 10); x += 1) {
          if (tiersMapped.includes(x)) {
            rules.push(tiersMapping[x]);
          } else if (mintPrice !== undefined && mintCurrency !== undefined) {
            rules.push({
              from: x,
              to: x,
              prices: mintPrice.map(value => parseFloat(value)),
              tokens: mintCurrency,
              minMint: 1,
            });
          }
        }

        setTiers(rules);

        return rules;
      }
    }

    return [];
  };

  const checkStarsApproval = async () => {
    let starsIsApproved = false;
    if (initialized) {
      const stars = await allowance(
        walletDetails.address ?? '',
        props.contractAddress ?? '',
      );

      const tierData = loadTierData();

      starsIsApproved =
        parseFloat(stars) >
        (props.prices && props.currency
          ? props.prices[props.currency.indexOf(currencyOption)] *
            parseFloat(amount)
          : 0);

      if (launchPadData?.collection?.tokenType === 'ERC1155 Mintable') {
        const { saleStrategy, mintCurrency, mintPrice } =
          launchPadData.collection;
        let starsPrice = 0;

        if (saleStrategy === 'Simplified') {
          const indexOfStars = mintCurrency?.indexOf('STARS');
          starsPrice =
            mintPrice !== undefined && indexOfStars !== undefined
              ? parseFloat(`${mintPrice[indexOfStars]}`) * parseFloat(amount)
              : 0;
        } else if (tierData.length > 0) {
          const indexOfStars = tierData[tierSelection].tokens.indexOf('STARS');
          starsPrice =
            parseFloat(
              `${tierData[tierSelection].prices[indexOfStars]}` || '0',
            ) * parseFloat(amount);
        }
        starsIsApproved = starsPrice > 0 && parseFloat(stars) > starsPrice;
      }
    }
    return starsIsApproved;
  };

  const tkType = launchPadData?.collection?.tokenType;
  const pricingStrategy =
    launchPadData?.collection?.saleStrategy ?? 'Simplified';

  const verifyQuantity = async (tokenId: number) => {
    const quantity: number = await getTokenIdSupply(tokenId);
    setTotalQuantity(quantity);
  };

  const loadModalData = async () => {
    let starsIsApproved = false;

    starsIsApproved = await checkStarsApproval();

    if (!starsIsApproved && currencyOption === 'MOGUL') {
      setButtonPurpose(buttonPurposes.approveStars);
    } else {
      setButtonPurpose(buttonPurposes.mint);
    }

    if (ERC1155ContractInitialized) {
      // initial Value of tokenId - If remains undefined are at the limit of minting
      let targetId: number = 0;

      const { maxSupply, maxQuantityGlobal, saleStrategy } =
        launchPadData?.collection || {
          maxSupply: 0,
          maxQuantityGlobal: 0,
          saleStrategy: 'Simplified',
        };

      // Simpliefied method can use last minted id
      // Tiered likely won't need more than 10 ids but method should be expanded in case they do
      const { supplies, lastMintedTokenId } = await getTotalSupplies(0, 10);

      // We get user balances to see if they have minted before - Need this to know if they can still claim whitelist
      let balances = await getUserBalances(0, 10, walletDetails.address ?? '');

      // Store our supply breakdown
      setTotalSupplies(supplies);

      if (
        pricingStrategy === 'Simplified' &&
        typeof lastMintedTokenId === 'number'
      ) {
        // We check if supply in our initial supply check to account for first time mint, else we go by last mintedToken
        setTotalQuantity(lastMintedTokenId);

        const hasMinted: boolean[] = [];

        supplies.forEach(v => {
          if (v > 0) {
            hasMinted.push(true);
          } else {
            hasMinted.push(false);
          }
        });

        setTargetTokenId(hasMinted.includes(true) ? lastMintedTokenId + 1 : 0);

        const promises: Promise<number[]>[] = [];
        const looprange = 100;
        // We need to verify balances for user here
        for (let x = 11; x < lastMintedTokenId; x += looprange) {
          promises.push(
            getUserBalances(x, x + looprange - 1, walletDetails.address ?? ''),
          );
        }

        const breakdown = await Promise.all(promises);

        breakdown.forEach(balance => {
          balances = [...balances, ...balance];
        });
      } else if (typeof supplies === 'object') {
        // We loop through the a little extra in case quota is filled in the previous one
        for (let x = 0; x < supplies.length; x += 1) {
          const tokenId = x;

          if (maxSupply && tokenId > Number(maxSupply)) {
            break;
          }

          const tokenIdSupply = supplies[tokenId];
          if (saleStrategy === 'Tiered') {
            // With tiers, we only want to target the proper tier
            const tierData = loadTierData();
            const targetTier = tierData[tierSelection];
            if (
              targetTier.from === tokenId &&
              ((Boolean(maxQuantityGlobal) &&
                tokenIdSupply < Number(maxQuantityGlobal)) ||
                !tokenIdSupply)
            ) {
              targetId = tokenId;
              break;
            }
            // Simplified pricing strategy start here
          } else if (tokenIdSupply === 0 || !tokenIdSupply) {
            targetId = tokenId;
            break;
          }
        }

        setTargetTokenId(targetId);
        void verifyQuantity(targetId);
      }

      if (balances.length > 0) {
        const userBalance = balances.reduce((sum, i) => sum + i, 0);
        // If proof is being provided as a prop then it mean whitelist period is active, not regular sale
        if (userBalance > 0 && props.proof.length > 0) {
          setCantMint(true);
          setMessageColor(messageColors.failure);
          setMintingMessage('Whitelist Mint already claimed');
          setMintLock(true);
        }
      }
    }
  };

  useEffect(() => {
    const collectionData = launchPadData?.collection;

    if (collectionData !== undefined && collectionData !== null && !mintLock) {
      const { maxSupply, maxQuantityGlobal, tokenType, saleStrategy } =
        collectionData;

      if (
        tokenType === 'ERC1155 Mintable' &&
        saleStrategy === 'Simplified' &&
        maxSupply !== undefined &&
        maxSupply !== null &&
        parseInt(maxSupply, 10) > 0 &&
        totalQuantity + parseInt(amount, 10) >= parseInt(maxSupply, 10)
      ) {
        setCantMint(true);
        setMessageColor(messageColors.failure);
        setMintingMessage('Not enough supply to mint amount');
      } else if (
        tokenType === 'ERC1155 Mintable' &&
        saleStrategy === 'Tiered' &&
        maxQuantityGlobal !== undefined &&
        maxQuantityGlobal !== null &&
        parseInt(maxQuantityGlobal, 10) > 0 &&
        totalQuantity + parseInt(amount, 10) > parseInt(maxQuantityGlobal, 10)
      ) {
        setCantMint(true);
        setMessageColor(messageColors.failure);
        setMintingMessage('Not enough supply in tier to mint amount');
      } else {
        setCantMint(false);
        setMessageColor(messageColors.success);
        setMintingMessage('');
      }
    }
  }, [amount, totalQuantity, launchPadData]);

  useEffect(() => {
    void (() => loadModalData())();
    loadTierData();
    setAmount(props.amount);
  }, [
    initialized,
    launchPadData,
    contractInitialized,
    ERC1155ContractInitialized,
  ]);

  useEffect(() => {
    if (currencyOption === 'MOGUL') {
      void checkStarsApproval();
    }
  }, [amount, currencyOption]);

  const updatedRadio = async (e: { target: any }) => {
    if (e.target.value === 'ETH') {
      setButtonPurpose(buttonPurposes.mint);
    } else if (e.target.value === 'STARS') {
      const starsIsApproved = await checkStarsApproval();
      if (starsIsApproved) {
        setButtonPurpose(buttonPurposes.mint);
      } else {
        setButtonPurpose(buttonPurposes.approveStars);
      }
    }
    setCurrencyOption(e.target.value);
  };

  const updateAmount = (e: { target: any }) => {
    const value = parseInt(e.target.value || '0', 10);

    if (value === 0) {
      setAmount('');
    } else if (!props.maxAmount) {
      setAmount(value.toString());
    } else if (value > 0 && value <= props.maxAmount) {
      setAmount(value.toString());
    } else if (value > 0 && value > props.maxAmount) {
      setAmount(props.maxAmount.toString());
    } else {
      setAmount('1');
    }
  };

  const submitPreflightCheck = () => {
    return amount && mintHash === '';
  };

  const ERC115MintableDefaultCurrencies =
    launchPadData?.collection?.mintCurrency;
  const ERC115MintableDefaultPrices = launchPadData?.collection?.mintPrice;
  const ERC1155MintableCurrencyTarget =
    pricingStrategy === 'Tiered' && tiers.length > 0
      ? tiers[tierSelection].tokens
      : ERC115MintableDefaultCurrencies;
  const ERC1155MintablePricesTarget =
    pricingStrategy === 'Tiered' && tiers.length > 0
      ? tiers[tierSelection].prices
      : ERC115MintableDefaultPrices;

  const paymentCurrencies: string[] | undefined =
    tkType === 'ERC1155 Mintable'
      ? ERC1155MintableCurrencyTarget
      : props.currency;

  const indexOfCurrency: number =
    paymentCurrencies?.indexOf(currencyOption) ?? 0;
  const prices =
    tkType === 'ERC1155 Mintable' ? ERC1155MintablePricesTarget : props.prices;
  const mintPrice = parseFloat(`${prices ? prices[indexOfCurrency] : 0}`);

  useEffect(() => {
    if (paymentCurrencies && !paymentCurrencies.includes(currencyOption)) {
      setCurrencyOption(paymentCurrencies[0]);
    }
  }, [paymentCurrencies, currencyOption, setCurrencyOption]);

  const regularMint = async () => {
    if (props.isPreSale) {
      try {
        const result = await mintPreSale(
          props.proof,
          amount,
          props.prices && props.currency?.includes('ETH')
            ? props.prices[props.currency.indexOf('ETH')].toString()
            : '0',
          currencyOption === 'MOGUL',
        );

        if (result.isSuccessful) {
          setMintHash(
            result.presale?.hash !== undefined ? result.presale.hash : '',
          );
          setMessageColor(messageColors.success);
          setMintingMessage('Transaction Submitted');
        } else {
          setMessageColor(messageColors.failure);
          if (
            result.error.message.includes(
              'Exceeds allowed presale you can mint',
            )
          ) {
            setMintingMessage('Exceeded Allowed Presale');
          } else if (result.error.message.includes('insufficient funds')) {
            setMintingMessage('Insufficient Funds To Mint');
          } else {
            setMintingMessage('Transaction Failed');
          }
        }
      } catch (e) {
        console.error(e);
        setMessageColor(messageColors.failure);
        setMintingMessage('Transaction Failed');
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const result = await mint(
          amount,
          props.prices && props.currency?.includes('ETH')
            ? props.prices[props.currency.indexOf('ETH')].toString()
            : '0',
          currencyOption === 'MOGUL',
        );
        if (result.isSuccessful) {
          setMintHash(result.sale?.hash !== undefined ? result.sale.hash : '');
          setMessageColor(messageColors.success);
          setMintingMessage('Transaction Submitted');
        } else if (result.error.message.includes('insufficient funds')) {
          setMintingMessage('Insufficient Funds To Mint');
        } else {
          setMessageColor(messageColors.failure);
          setMintingMessage('Transaction Failed');
        }
      } catch (e) {
        console.error(e);
        setMessageColor(messageColors.failure);
        setMintingMessage('Transaction Failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const mintERC11551155Mintable = async () => {
    try {
      // We pass our minting function
      // - the tokenId we want to start minting from
      // - the amount we want to mint
      // - the pricing strategy it's supposed to use
      const result = await mintLaunchpadTokens(
        targetTokenId,
        parseInt(amount, 10),
        currencyOption === 'MOGUL'
          ? starsAddress ?? ''
          : '0x0000000000000000000000000000000000000000',
        pricingStrategy,
        props.proof,
      );

      if (result.isSuccessful) {
        /*setMintHash(
          result.presale?.hash !== undefined ? result.presale.hash : '',
        ); */
        setMessageColor(messageColors.success);
        setMintingMessage('Transaction Submitted');
      } else {
        setMessageColor(messageColors.failure);
        if (
          result.error?.message.includes('Exceeds allowed presale you can mint')
        ) {
          setMintingMessage('Exceeded Allowed Presale');
        } else if (result.error?.message.includes('insufficient funds')) {
          setMintingMessage('Insufficient Funds To Mint');
        } else {
          setMintingMessage('Transaction Failed');
        }

        await loadModalData();
      }
    } catch (e) {
      console.error(e);
      setMessageColor(messageColors.failure);
      setMintingMessage('Transaction Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMintNft = async () => {
    if (!contractInitialized || !initialized) {
      setMessageColor(messageColors.failure);
      setMintingMessage('Session Error: Please Refresh The Page');
      return;
    }

    if (props.chainId !== walletDetails.chain.type) {
      setMessageColor(messageColors.failure);
      setMintingMessage(
        `Digital Collectible Must Be Minted On ${props.chainId}`,
      );
      return;
    }

    if (buttonPurpose === buttonPurposes.approveStars) {
      setLoading(true);
      try {
        const isApproved = await approve(
          props.contractAddress ?? '',
          ethers.constants.MaxUint256,
        );
        if (isApproved) {
          setMessageColor(messageColors.success);
          setMintingMessage('Approval Success');
          setButtonPurpose(buttonPurposes.mint);
        } else {
          setMessageColor(messageColors.failure);
          setMintingMessage('Approval Failed');
        }
      } catch (e) {
        console.error(e);
        setMessageColor(messageColors.failure);
        setMintingMessage('Approval Failed');
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);

      if (tkType === 'ERC1155 Mintable') {
        await mintERC11551155Mintable();
      } else {
        await regularMint();
      }
    }
  };

  return (
    <Modal
      open={true}
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
    >
      <Box
        className={`${styles.mintModalContainer} w-80 flex flex-col items-center`}
      >
        <div className="flex w-full justify-center">
          <div
            className={`${styles.modalTitle} flex text-3xl font-bold text-center`}
          >
            Minting
          </div>
          <div
            role="button"
            aria-hidden
            className={styles.xIconBG + ' ml-auto'}
            onClick={props.hideModal}
          >
            <XIcon className={styles.xIcon} />
          </div>
        </div>

        {mintHash === '' ? (
          <Box
            className={`${styles.mintModalSubContainer} pt-8 w-full h-auto flex flex-col items-center rounded-md`}
          >
            {loading && (
              <CircularProgress
                className="w-20 -mt-1"
                size={16}
                variant="indeterminate"
                style={{ color: '#F5D659' }}
              />
            )}
            {!loading && (
              <p
                className={`${styles.mintMessage} mb-2 ml-5 mr-5`}
                style={{ color: messageColor }}
              >
                {mintingMessage}
              </p>
            )}
            <div className={`${styles.modalInputContainer} mx-1 my-1`}>
              <p className={`${styles.modalLabels}`}>Contract Address</p>
              <input
                type={'text'}
                value={props.contractAddress}
                className={`${styles.modalInput}`}
                placeholder={'Contract Address'}
                disabled={true}
              />
            </div>

            <div className={`${styles.modalInputContainer} mx-1 my-1`}>
              <p className={`${styles.modalLabels}`}>Amount</p>
              <input
                type={'number'}
                value={amount}
                pattern="[0-9]"
                className={`${styles.modalInput}`}
                placeholder={'Amount'}
                min="1"
                onChange={updateAmount}
              />
            </div>

            <div
              className={`${styles.modalInputContainer} mx-1 my-1`}
              style={{ marginLeft: 10 }}
            >
              <p>
                {tkType === 'ERC1155 Mintable' &&
                  `${pricingStrategy}` === 'Tiered' && (
                    <Grid md={8}>
                      <TextField
                        label="Select Tier"
                        select
                        value={tierSelection}
                        onChange={event => {
                          const target = parseInt(event.target.value, 10);
                          setTierSelection(parseInt(event.target.value, 10));
                          setTargetTokenId(tiers[target || 0].from);
                          void verifyQuantity(tiers[target || 0].from);
                        }}
                        fullWidth
                      >
                        {tiers.map((tier, i) => {
                          return (
                            <MenuItem
                              key={`${tier.to}-${tier.from}-${i}`}
                              value={i}
                            >
                              {`Tier ${tier.from}; ${tier.tokens
                                .map(
                                  (token, index) =>
                                    `${tier.prices[index]} ${token}`,
                                )
                                .join()}`}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                  )}
              </p>
              <br />
            </div>

            <div className={`${styles.modalInputContainer} mx-1 my-1`}>
              <div
                className="flex items-center mt-1 mb-2"
                onChange={updatedRadio}
              >
                {(paymentCurrencies ?? []).map((curr, index) => {
                  return (
                    <div className="flex items-center" key={index}>
                      <input
                        id={curr}
                        type="radio"
                        name={curr}
                        value={curr}
                        checked={currencyOption === curr}
                        onChange={() => {}}
                      />
                      <label
                        htmlFor={curr}
                        className={styles.radioLabel + ' text-xs ml-1 mr-2'}
                      >
                        {curr.toUpperCase()}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={`${styles.modalInputContainer} mx-1 my-1`}>
              {tkType === 'ERC1155 Mintable' && (
                <p className={`${styles.total}`}>
                  {`Total: ${(mintPrice * parseFloat(amount || '0')).toFixed(
                    4,
                  )} ${currencyOption}`}
                </p>
              )}

              {tkType !== 'ERC1155 Mintable' && (
                <p className={`${styles.total}`}>
                  Total:{' '}
                  {props.prices && props.currency && amount
                    ? (
                        props.prices[props.currency.indexOf(currencyOption)] *
                        parseFloat(amount)
                      ).toFixed(2)
                    : '0'}{' '}
                  {currencyOption}
                </p>
              )}
            </div>
            <br />
            <button
              className={
                submitPreflightCheck() && !loading && !cantMint && !mintLock
                  ? `${styles.submitButton} mb-8`
                  : `${styles.disabledButton} mb-8`
              }
              disabled={loading || cantMint || mintLock}
              onClick={handleMintNft}
            >
              {buttonPurpose}
            </button>
          </Box>
        ) : (
          <Box
            className={`${styles.mintModalSubContainer} w-full h-auto flex flex-col justify-center items-center rounded-md`}
          >
            <div
              className={
                styles.checkmarkBack +
                ' w-28 h-28 mb-4 rounded-full flex justify-center items-center'
              }
            >
              <img className="w-20" src={checkmark} alt="checkmark" />
            </div>
            <p
              className={`${styles.mintMessage} my-2 ml-5 mr-5`}
              style={{ color: '#fbd357' }}
            >
              {mintingMessage}
            </p>
            <a
              className={styles.mintReceipt}
              target="_blank"
              rel="noreferrer"
              href={'https://rinkeby.etherscan.io/tx/' + mintHash}
            >
              {mintHash.slice(0, 25)}...
            </a>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export const SetMintingModal =
  (
    contractAddress: string | undefined,
    amount: string,
    maxAmount: number,
    chainId: string,
    prices: number[] | undefined,
    currency: string[] | undefined,
    isPreSale: boolean,
    proof: BytesLike[],
    launchPadData: LaunchPadData,
  ) =>
  ({ hideModal }: MintingModalProps) => {
    return (
      <MintingModal
        contractAddress={contractAddress}
        amount={amount}
        maxAmount={maxAmount}
        chainId={chainId}
        prices={prices}
        currency={currency}
        isPreSale={isPreSale}
        proof={proof}
        hideModal={hideModal}
        launchPadData={launchPadData}
      />
    );
  };
